import { getLetterByIndex } from "../../../utils/methods";
import { StageDetails } from "../../../types/tournament";
import { GroupDefinition } from "../types";
import { Button, Card, Field, Select, Tag } from "../../../ui";
import StepHeader from "./StepHeader";

interface Props {
  stage?: StageDetails;
  selectedTeams: string[];
  numOfGroups: number;
  setNumOfGroups: (v: number) => void;
  groups: GroupDefinition[];
  setGroups: React.Dispatch<React.SetStateAction<GroupDefinition[]>>;
  getTeamGroup: (teamId: string) => number;
  handleGroupChange: (teamId: string, groupIndex: number) => void;
}

const ManageGroupsStep = ({
  stage, selectedTeams, numOfGroups, setNumOfGroups, groups, setGroups, getTeamGroup, handleGroupChange,
}: Props) => (
  <div className="cs-step manage-groups-container">
    <StepHeader title="Manage Groups" subtitle="First assign strong teams to balance groups, then fill in the rest" />

    <Card className="groups-configuration">
      <Field label="How many groups in this stage?" className="cs-field-narrow">
        {(fieldId) => (
          <Select id={fieldId} block value={numOfGroups} onChange={(e) => setNumOfGroups(Number(e.target.value))}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item, id) => (
              <option value={item} key={id}>{item}</option>
            ))}
          </Select>
        )}
      </Field>
    </Card>

    <Card title="Groups Overview" className="groups-overview">
      <div className="groups-summary">
        {groups.map((group) => (
          <div key={group.groupName} className="group-summary-card">
            <div className="group-header">
              <span className="group-name">Group {group.groupName}</span>
              <Tag tone={group.teamIds.length > 0 ? "brand" : "neutral"} className="team-count">
                {group.teamIds.length} teams
              </Tag>
            </div>
            <div className="group-teams-preview">
              {group.teamIds.length > 0 ? (
                group.teamIds.slice(0, 5).map((teamId) => {
                  const team = stage?.teams.find((t) => t.id === teamId);
                  return <span key={teamId} className="team-preview">{team?.teamName}</span>;
                })
              ) : (
                <span className="empty-group">No teams assigned</span>
              )}
              {group.teamIds.length > 5 && <span className="more-teams">+{group.teamIds.length - 5} more</span>}
            </div>
          </div>
        ))}
      </div>
    </Card>

    <Card
      className="teams-assignment-section"
      padding="none"
      title="Assign Teams to Groups"
      subtitle={
        <span className="teams-count">
          {selectedTeams.length - groups.flatMap((g) => g.teamIds).length} unassigned,{" "}
          {groups.flatMap((g) => g.teamIds).length} assigned
        </span>
      }
      actions={
        <>
          <Button
            size="sm"
            variant="tint"
            className="quick-action-btn"
            onClick={() => {
              const assignedTeamIds = new Set(groups.flatMap((g) => g.teamIds));
              const unassignedTeams = selectedTeams.filter((teamId) => !assignedTeamIds.has(teamId));
              if (unassignedTeams.length === 0) return;
              const newGroups = groups.map((group) => ({ ...group }));
              unassignedTeams.forEach((teamId, teamIndex) => {
                const groupIndex = teamIndex % numOfGroups;
                newGroups[groupIndex].teamIds.push(teamId);
              });
              setGroups(newGroups);
            }}
            disabled={selectedTeams.length === groups.flatMap((g) => g.teamIds).length}
          >
            Fill Remaining Teams
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="quick-action-btn"
            onClick={() => setGroups(groups.map((group) => ({ ...group, teamIds: [] })))}
          >
            Clear All
          </Button>
        </>
      }
    >
      <div className="teams-list">
        {selectedTeams
          .sort((a, b) => {
            const groupIndexA = getTeamGroup(a);
            const groupIndexB = getTeamGroup(b);
            if (groupIndexA === -1 && groupIndexB !== -1) return -1;
            if (groupIndexA !== -1 && groupIndexB === -1) return 1;
            return 0;
          })
          .map((team, teamId) => {
            const currentTeam = stage?.teams.find((x) => x.id === team);
            const currentGroupIndex = getTeamGroup(currentTeam?.id!);
            return (
              <div key={teamId} className={`team-container ${currentGroupIndex === -1 ? "unassigned" : ""}`}>
                <div className="team-details">
                  <div className="team-info">
                    <p className="name">{currentTeam?.teamName}</p>
                    <p className="captain">{currentTeam?.captainName}</p>
                  </div>
                  <div className="current-group">
                    <span className="group-label">Currently in:</span>
                    {currentGroupIndex >= 0 ? (
                      <Tag tone="brand" className="group-badge">Group {getLetterByIndex(currentGroupIndex)}</Tag>
                    ) : (
                      <Tag tone="warn" className="group-badge no-group">No group assigned</Tag>
                    )}
                  </div>
                </div>
                <div className="group-selector">
                  <span className="group-label">Assign to:</span>
                  <Select
                    size="sm"
                    aria-label={`Assign ${currentTeam?.teamName ?? "team"} to a group`}
                    className="group-select"
                    value={currentGroupIndex >= 0 ? currentGroupIndex : ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleGroupChange(currentTeam?.id!, value === "" ? -1 : Number(value));
                    }}
                  >
                    <option value="">-- select a group --</option>
                    {Array(numOfGroups).fill(0).map((_, groupId) => (
                      <option key={groupId} value={groupId}>Group {getLetterByIndex(groupId)}</option>
                    ))}
                  </Select>
                </div>
              </div>
            );
          })}
      </div>
    </Card>
  </div>
);

export default ManageGroupsStep;
